/**
 * Evals API methods
 * Handles all eval-related API calls
 */

import {
  extractArrayFromResponse,
  extractIdFromLocation,
  getBasePath,
  normalizeTimestamp,
  createNeedsRefetchError
} from './base';
import { normalizeAvailableScorersPayload } from './normalizeAvailableScorers';
import type {
  Eval,
  DatasetEval,
  DatasetEvalsResponse,
  EvalsPageResponse,
  Scorer
} from './types';

import axiosInstance from '~/common/helpers/axiosInstance';
import { SCORER_LLM } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/constants/evalConstants';
import {
  evalLlmFlatFieldsFromConfiguration,
  type EvalLlmConfiguration
} from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/ComponentSettings/components/ComponentExecution/utils/evalLlmMapper';

/**
 * Normalizes expected result for API payload.
 *
 * Handles backward compatibility with old format where simple values were wrapped in { value: ... }.
 * The new format should pass primitive values directly, but we still normalize old format for compatibility.
 *
 * Also handles cases where a string was incorrectly spread into an object with numeric keys.
 */
function normalizeExpectedResultForApi(
  expectedResult:
    | Record<string, unknown>
    | string
    | number
    | boolean
    | null
    | undefined
    | unknown[]
): unknown {
  // Ensure we never send undefined to the API (backend may NPE on missing expectedResult)
  if (expectedResult === undefined) {
    return null;
  }

  // If it's already a primitive or array, return as-is (new format)
  if (
    typeof expectedResult === 'string' ||
    typeof expectedResult === 'number' ||
    typeof expectedResult === 'boolean' ||
    expectedResult === null ||
    Array.isArray(expectedResult)
  ) {
    return expectedResult;
  }

  // If it's not an object, return as-is
  if (typeof expectedResult !== 'object' || expectedResult === null) {
    return expectedResult;
  }

  const keys = Object.keys(expectedResult);

  // Check if object has exactly one key "value" (old format)
  if (keys.length === 1 && keys[0] === 'value') {
    const v = expectedResult.value;
    if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean' ||
      v === null
    ) {
      return v;
    }
  }

  // Handle case where string was incorrectly spread: { "0": "B", "1": "R", "2": "L", "value": "BRL" }
  // Check if object has "value" key and all other keys are numeric
  if ('value' in expectedResult) {
    const nonValueKeys = keys.filter(k => k !== 'value');
    const allNumericKeys = nonValueKeys.every(k => /^\d+$/.test(k));

    if (allNumericKeys) {
      const v = expectedResult.value;
      if (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean' ||
        v === null
      ) {
        return v;
      }
    }
  }

  // For complex objects (RANGE, ALL_ITEMS_MATCH, etc.), return as-is
  return expectedResult;
}

/** Map raw API item to Eval (shared by list, create/update responses, etc.) */
export function mapRawItemToEval(item: unknown): Eval {
  const evalItem = (item ?? {}) as Record<string, unknown>;
  const evalId = evalItem.evalId as string | undefined;
  const id = (evalItem.id as string) || evalId || '';
  const scorer = (evalItem.scorer as string) || '';
  const base: Eval = {
    id,
    name: (evalItem.name as string) || (evalItem.evalName as string) || '',
    path: evalItem.path as string,
    scorer,
    variant: evalItem.variant as string,
    llmTemplate: (evalItem.llmTemplate as string) || undefined,
    llmModel: (evalItem.llmModel as string) || undefined,
    llmPrompt: (evalItem.llmPrompt as string) || undefined,
    llmModelConfig:
      (evalItem.llmModelConfig as Record<string, unknown>) || undefined,
    enabled:
      evalItem.enabled !== undefined ? (evalItem.enabled as boolean) : true,
    createdAt: evalItem.createdAt
      ? normalizeTimestamp(evalItem.createdAt)
      : undefined,
    updatedAt: evalItem.updatedAt
      ? normalizeTimestamp(evalItem.updatedAt)
      : undefined
  };

  if (
    scorer === SCORER_LLM &&
    evalItem.configuration &&
    typeof evalItem.configuration === 'object'
  ) {
    const llm = evalLlmFlatFieldsFromConfiguration(
      evalItem.configuration as EvalLlmConfiguration
    );
    return {
      ...base,
      llmTemplate: llm.llmTemplate ?? base.llmTemplate,
      llmModel: llm.llmModel || base.llmModel,
      llmPrompt: llm.llmPrompt,
      llmModelConfig: llm.llmModelConfig as Record<string, unknown>
    };
  }

  return base;
}

/**
 * Create a new eval
 */
export const createEval = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  data: Record<string, unknown> & {
    name: string;
    path: string;
    scorer: string;
    variant: string;
  }
): Promise<Eval> => {
  // Validate required parameters
  if (!realm || !pipelineName || !connectorId) {
    throw new Error(
      `Missing required parameters: realm=${realm}, pipelineName=${pipelineName}, connectorId=${connectorId}`
    );
  }

  // Ensure version is valid
  const validVersion = version && version > 0 ? version : 1;
  const path = `${getBasePath(realm, pipelineName, validVersion, connectorId)}/evals`;

  const response = await axiosInstance.post<Eval>(path, data);

  // Handle case where API returns empty string but eval was created (201 Created)
  const responseData: unknown = response.data;
  const isEmptyString =
    typeof responseData === 'string' && responseData.trim() === '';
  if (!responseData || isEmptyString) {
    const evalId = extractIdFromLocation(response.headers?.location, 'evals');

    if (!evalId) {
      throw createNeedsRefetchError(
        'Eval created but ID not available in response. Please refetch evals.',
        data.name
      );
    }

    // Construct eval object from request data and response
    // Use current timestamp in milliseconds for createdAt/updatedAt
    const now = Date.now();
    const configuration = data.configuration as
      | EvalLlmConfiguration
      | undefined;
    const llmFromConfig =
      data.scorer === SCORER_LLM && configuration
        ? evalLlmFlatFieldsFromConfiguration(configuration)
        : undefined;

    const constructedEval: Eval = {
      id: evalId,
      name: data.name,
      path: data.path,
      scorer: data.scorer,
      variant: data.variant,
      llmTemplate:
        llmFromConfig?.llmTemplate ??
        configuration?.llmTemplate ??
        (data as { llmTemplate?: string }).llmTemplate ??
        undefined,
      llmModel: llmFromConfig?.llmModel || configuration?.model,
      llmPrompt: llmFromConfig
        ? llmFromConfig.llmPrompt
        : configuration?.systemPrompt,
      llmModelConfig: llmFromConfig?.llmModelConfig as
        | Record<string, unknown>
        | undefined,
      enabled: true,
      createdAt: now,
      updatedAt: now
    };

    return constructedEval;
  }

  return mapRawItemToEval(response.data);
};

export interface GetEvalsPaginatedParams {
  page?: number;
  size?: number;
  sort?: string;
  dir?: 'ASC' | 'DESC';
}

/**
 * List evals with pagination
 * GET /evals?page=0&size=20&sort=updatedAt&dir=DESC
 */
export const getEvalsPaginated = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  params: GetEvalsPaginatedParams = {}
): Promise<EvalsPageResponse> => {
  const { page = 0, size = 20, sort = 'updatedAt', dir = 'DESC' } = params;

  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/evals`;
  const response = await axiosInstance.get<unknown>(path, {
    params: { page, size, sort, dir }
  });

  const data = response.data as Record<string, unknown>;
  const contentRaw = Array.isArray(data?.content)
    ? (data.content as unknown[])
    : extractArrayFromResponse<unknown>(response.data);

  const content = contentRaw.map(mapRawItemToEval);

  return {
    content,
    totalElements: (data.totalElements as number) ?? content.length,
    totalPages: (data.totalPages as number) ?? 1,
    number: (data.number as number) ?? 0,
    size: (data.size as number) ?? size,
    first: (data.first as boolean) ?? true,
    last: (data.last as boolean) ?? true,
    numberOfElements: (data.numberOfElements as number) ?? content.length,
    empty: (data.empty as boolean) ?? content.length === 0
  };
};

/**
 * List all evals (backward compatible)
 * Uses pagination with a large page size so other consumers get the full list.
 */
export const getEvals = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string
): Promise<Eval[]> => {
  const page = await getEvalsPaginated(
    realm,
    pipelineName,
    version,
    connectorId,
    {
      page: 0,
      size: 1000,
      sort: 'updatedAt',
      dir: 'DESC'
    }
  );
  return page.content;
};

/**
 * Update an eval template
 * Updates the eval template properties (name, path, scorer, variant)
 * Changes are automatically reflected in all datasets using this eval
 */
export const updateEval = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  evalId: string,
  data: Record<string, unknown> & {
    name: string;
    path: string;
    scorer: string;
    variant: string;
  }
): Promise<Eval> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/evals/${encodeURIComponent(evalId)}`;
  const response = await axiosInstance.put<Eval>(path, data);

  return mapRawItemToEval(response.data);
};

/**
 * Disable an eval
 */
export const disableEval = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  evalId: string
): Promise<Eval> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/evals/${encodeURIComponent(evalId)}/disable`;
  const response = await axiosInstance.post<Eval>(path);

  return mapRawItemToEval(response.data);
};

/**
 * Enable an eval
 */
export const enableEval = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  evalId: string
): Promise<Eval> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/evals/${encodeURIComponent(evalId)}/enable`;
  const response = await axiosInstance.post<Eval>(path);

  return mapRawItemToEval(response.data);
};

/**
 * Delete an eval
 */
export const deleteEval = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  evalId: string
): Promise<void> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/evals/${encodeURIComponent(evalId)}`;
  await axiosInstance.delete(path);
};

/**
 * Get available scorers and their variants
 * Returns all scorer types with their display names and available variants
 *
 * @returns Array of scorer objects with name, displayName, and variants (name + requiredParameters)
 */
export const getAvailableScorers = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string
): Promise<Scorer[]> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/evals/scorers`;

  const response = await axiosInstance.get<unknown>(path);
  return normalizeAvailableScorersPayload(response.data);
};

/**
 * Get evals for a dataset (same endpoint, no pagination).
 * Returns dataset evals with expected results as a single response object.
 */
export const getDatasetEvals = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  options?: { cacheBust?: boolean }
): Promise<DatasetEvalsResponse> => {
  let path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/evals`;
  if (options?.cacheBust) {
    path += `?_t=${Date.now()}`;
  }
  const response = await axiosInstance.get<unknown>(path);
  const dataArray = extractArrayFromResponse<unknown>(response.data);

  const content: DatasetEval[] = dataArray.map((item: unknown) => {
    const raw = item as Record<string, unknown>;
    const evalObj = (raw.eval ?? raw) as Record<string, unknown>;
    const evalId = (raw.evalId ?? evalObj.id ?? raw.id) as string;
    const id = (raw.id ?? raw.evalId) as string;

    // This endpoint only returns slim eval summaries (no `configuration`),
    // so we still try to recover LLM-specific fields from `configuration`
    // when present (some environments hydrate it), but the UI must cross-
    // reference `GET /evals` to fully enrich the LLM judge prompt. See
    // `useDatasetEvalsManagement` for the enrichment step.
    const scorer = (evalObj.scorer ?? raw.scorer) as string;
    const llmConfiguration =
      scorer === SCORER_LLM &&
      evalObj.configuration &&
      typeof evalObj.configuration === 'object'
        ? evalLlmFlatFieldsFromConfiguration(
            evalObj.configuration as EvalLlmConfiguration
          )
        : undefined;

    return {
      id,
      datasetId: (raw.datasetId ?? datasetId) as string,
      evalId,
      eval: {
        id: evalId,
        name: (evalObj.name ?? raw.name) as string,
        path: (evalObj.path ?? raw.path) as string,
        scorer,
        variant: (evalObj.variant ?? raw.variant) as string,
        enabled: (() => {
          if (evalObj.enabled !== undefined) return evalObj.enabled as boolean;
          if (raw.enabled !== undefined) return raw.enabled as boolean;
          return true;
        })(),
        llmTemplate:
          llmConfiguration?.llmTemplate ??
          (evalObj.llmTemplate as string | undefined),
        llmModel:
          llmConfiguration?.llmModel ??
          (evalObj.llmModel as string | undefined),
        llmPrompt:
          llmConfiguration?.llmPrompt ??
          (evalObj.llmPrompt as string | undefined),
        llmModelConfig:
          (llmConfiguration?.llmModelConfig as
            | Record<string, unknown>
            | undefined) ??
          (evalObj.llmModelConfig as Record<string, unknown> | undefined),
        createdAt:
          (evalObj.createdAt ?? raw.createdAt)
            ? normalizeTimestamp(evalObj.createdAt ?? raw.createdAt)
            : undefined,
        updatedAt:
          (evalObj.updatedAt ?? raw.updatedAt)
            ? normalizeTimestamp(evalObj.updatedAt ?? raw.updatedAt)
            : undefined
      },
      expectedResult: (raw.expectedResult as Record<string, unknown>) ?? {},
      version: (() => {
        const rawRecord = raw as Record<string, unknown>;
        const v =
          raw.version ?? rawRecord['_version'] ?? rawRecord.entityVersion; // eslint-disable-line dot-notation -- _version is API field name
        if (typeof v === 'number' && !Number.isNaN(v)) return v;
        if (typeof v === 'string') {
          const n = parseInt(v, 10);
          if (!Number.isNaN(n)) return n;
        }
        return undefined;
      })(),
      createdAt: normalizeTimestamp(raw.createdAt ?? 0),
      updatedAt: normalizeTimestamp(raw.updatedAt ?? 0)
    } as DatasetEval;
  });

  return {
    content,
    totalElements: content.length,
    totalPages: 1,
    number: 0,
    size: content.length,
    first: true,
    last: true,
    numberOfElements: content.length,
    empty: content.length === 0
  };
};

/**
 * Add eval to dataset
 *
 * @param expectedResult - The expected result JSON object. Structure varies by scorer/variant:
 *   - Most variants: { value: any }
 *   - NUMBER.RANGE: { min: number, max: number }
 *   - ARRAY.ALL_ITEMS_MATCH: { condition: string, value: any }
 *   - ARRAY.LENGTH_BETWEEN: { min: number, max: number }
 *   - EMPTY/NOT_EMPTY variants: {}
 */
export const addEvalToDataset = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  evalId: string,
  expectedResult:
    | Record<string, unknown>
    | string
    | number
    | boolean
    | null
    | unknown[]
): Promise<void> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/evals`;
  await axiosInstance.post(path, {
    evalId,
    expectedResult: normalizeExpectedResultForApi(expectedResult)
  });
};

/**
 * Update dataset eval expected result
 * Uses PUT /datasets/{datasetId}/evals/{datasetEvalId} as documented in the API
 *
 * @param datasetEvalId - The dataset eval ID (not the eval ID)
 * @param expectedResult - The expected result JSON object. Structure varies by scorer/variant:
 *   - Most variants: primitive or { value: any }
 *   - NUMBER.RANGE: { min: number, max: number }
 *   - NUMBER.EXACT_TOLERANCE: { value: number, tolerance: number }
 *   - ARRAY.ALL_ITEMS_MATCH: { condition: string, value: any }
 *   - ARRAY.LENGTH_BETWEEN: { min: number, max: number }
 *   - EMPTY/NOT_EMPTY variants: null
 */
export const updateDatasetEval = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  datasetEvalId: string,
  expectedResult:
    | Record<string, unknown>
    | string
    | number
    | boolean
    | null
    | unknown[],
  entityVersion?: number
): Promise<DatasetEval> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/evals/${encodeURIComponent(datasetEvalId)}`;
  const normalized = normalizeExpectedResultForApi(expectedResult) ?? null;
  const body: {
    expectedResult: Record<string, unknown> | null;
    version?: number;
  } = {
    expectedResult: normalized as Record<string, unknown> | null
  };
  if (entityVersion != null) {
    body.version = entityVersion;
  }
  const response = await axiosInstance.put<DatasetEval>(path, body);

  // Normalize timestamps in response
  return {
    ...response.data,
    eval: {
      ...response.data.eval,
      createdAt: response.data.eval.createdAt
        ? normalizeTimestamp(response.data.eval.createdAt)
        : undefined,
      updatedAt: response.data.eval.updatedAt
        ? normalizeTimestamp(response.data.eval.updatedAt)
        : undefined
    },
    createdAt: normalizeTimestamp(response.data.createdAt),
    updatedAt: normalizeTimestamp(response.data.updatedAt)
  };
};

/**
 * Update expected result for a specific experiment and eval
 * This function finds the datasetEvalId from the evalId and uses the documented endpoint format
 * Note: Uses PUT method and datasetEvalId in the path, matching the documented API pattern
 *
 * @param expectedResult - The expected result JSON object. Structure varies by scorer/variant:
 *   - Most variants: primitive or { value: any }
 *   - NUMBER.RANGE: { min: number, max: number }
 *   - NUMBER.EXACT_TOLERANCE: { value: number, tolerance: number }
 *   - ARRAY.ALL_ITEMS_MATCH: { condition: string, value: any }
 *   - ARRAY.LENGTH_BETWEEN: { min: number, max: number }
 *   - EMPTY/NOT_EMPTY variants: null
 */
export const updateExperimentEvalValue = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  experimentId: string,
  evalId: string,
  expectedResult:
    | Record<string, unknown>
    | string
    | number
    | boolean
    | null
    | unknown[]
): Promise<void> => {
  // First, get the dataset evals to find the datasetEvalId
  const datasetEvals = await getDatasetEvals(
    realm,
    pipelineName,
    version,
    connectorId,
    datasetId
  );

  // Ensure content exists and is an array
  if (!datasetEvals || !Array.isArray(datasetEvals.content)) {
    throw new Error(`Failed to fetch dataset evals for dataset: ${datasetId}`);
  }

  // Find the dataset eval that matches the evalId
  const datasetEval = datasetEvals.content.find(de => de.evalId === evalId);

  if (!datasetEval) {
    // This is an expected scenario - the eval may not be added to the dataset yet
    // The caller should check if the eval exists before calling this function
    throw new Error(
      `Dataset eval not found for evalId: ${evalId} in dataset: ${datasetId}. ` +
        `The eval must be added to the dataset first before setting experiment-specific values. ` +
        `Found ${datasetEvals.content.length} evals in dataset.`
    );
  }

  // Use PUT method with datasetEvalId in the path, matching documented API pattern
  // For experiment-specific expected results, we use the experiment path with datasetEvalId
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/experiments/${encodeURIComponent(experimentId)}/evals/${encodeURIComponent(datasetEval.id)}`;

  const normalized = normalizeExpectedResultForApi(expectedResult) ?? null;
  await axiosInstance.put(path, { expectedResult: normalized });
};

/**
 * Remove eval from dataset
 * Removes the dataset eval association using the datasetEvalId
 *
 * @param datasetEvalId - The dataset eval ID (not the eval template ID)
 */
export const removeEvalFromDataset = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  datasetEvalId: string
): Promise<void> => {
  // Use DELETE method with datasetEvalId in the path
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/evals/${encodeURIComponent(datasetEvalId)}`;
  await axiosInstance.delete(path);
};
